/*
  dp-data-upload-shell.js — Theme-Hülle um das FLYERALARM-Upload-Popup
  (sections/dp-data-upload.liquid, Vendor-Skript data-upload.js, KI-1/KI-35).
  Das Vendor-Skript bleibt unverändert; die Hülle hängt sich an dessen DOM und
  an zwei statische API-Methoden (transparenter Tee, kein Request mehr):
  Klartext je Zeile inkl. sichtbarer Ablehnungsgründe · Du-Form für Vendor-
  Texte · data-label/data-dp-col für die Mobil-Karten · Hinweisleiste zum
  Wiedereinstieg · Kasse-Hinweis/Primär-Button · Fokus, Fokusfalle, Rückgabe.
  Kaufweg verkürzen (2026-09-14): lebende Wartezeit je „Wird geprüft“-Zeile
  („Prüfung läuft seit 0:42 · meist 1–3 Minuten“, Poll-Takt des Vendors bleibt) ·
  Auto-Kasse: wird ein Auftrag dieser Browsersitzung angenommen, zählt ein
  5-s-Countdown mit „Abbrechen“ und klickt dann den Vendor-Link „Kasse“ (dessen
  onclick → handleAddToCart(row) → /api/duplicate → cart/add → /cart); einmal je
  Auftrag (sessionStorage) · auf /cart: liegt genau eine Position im Warenkorb
  und kam der Kunde gerade über „Kasse“, nach 3 s weiter zu /checkout (Setting
  auto_checkout, „Abbrechen“). Reine Helfer sind exportiert (tests/designer).
  Schwebender Trigger (Audit 2026-09-16, P1-7): startet im Markup mit hidden und
  erscheint nur für Besucher mit Upload-Historie – buyer_session_id lag VOR dem
  Vendor-Skript im localStorage, oder dp:order-intent / dp:upload-arrival stehen
  in der Sitzung, oder der Tee hat mindestens einen Auftrag geliefert. Auf
  /pages/designer nie (triggerVisible, syncTrigger).
  Nie angefasst (das Vendor-Skript liest es): textContent von
  .flyeralarm-action-link, className des .flyeralarm-status-badge, die
  Tooltip-Elemente im Badge, IDs/Klassen aus dp-data-upload.liquid.
  Vendor-Skript nachladen (Technik-Prüfung 2026-09-16, P1-2): data-upload.js
  (55 KB, Cloudinary) steht nicht mehr als <script> in der Section, sondern
  wird hier nur geladen, wenn der Besucher es brauchen kann – Ankunft über
  ?view=upload-product-data, buyer_session_id im localStorage, dp:order-intent
  oder dp:upload-arrival in der Sitzung, oder Warenkorbseite (shouldLoadVendor).
  Die Host-Umleitung (dp-data-upload-hosts.js) bleibt statisch und läuft davor.
  Nach dem Load-Event des Skripts: Tee auf die Vendor-Klasse (installTee), dann
  die Instanz – der Vendor hängt sie selbst an DOMContentLoaded; ist das Event
  schon vorbei, erzeugt bootVendor sie (new FlyeralarmDataUpload). Bei onerror
  bleibt der Trigger verborgen, die Konsole warnt; kein toter Knopf.
  Escape schließt den Dialog über den Vendor-Schließen-Knopf (handleDialogKeydown).
*/

import { renderFlowSteps } from './dp-flow-steps.js?v=6c1534790e';

const S = {
  wrapper: '.dp-data-upload',
  block: '.flyeralarm-popup-block',
  trigger: '.flyeralarm-popup-trigger',
  overlay: '.flyeralarm-popup-overlay',
  container: '.flyeralarm-popup-container',
  title: '.flyeralarm-popup-title',
  closeBtn: '.flyeralarm-popup-close',
  tbody: '.flyeralarm-status-table tbody',
  thead: '.flyeralarm-status-table thead th',
  badge: '.flyeralarm-status-badge',
  actionLink: '.flyeralarm-action-link',
  notificationBadge: '#flyeralarm-notification-badge',
  live: '#dp-upload-live',
};

const COLUMNS = ['product-id', 'product-name', 'status', 'uploads', 'datasheet', 'date', 'actions'];
/** Abfrageintervall des Prüfstatus (Vendor-Standard 10 s). */
export const STATUS_REFRESH_MS = 4000;

/** „Kalender DIN A4 hoch 569e951e“ → „Kalender DIN A4 hoch“ (Kurz-ID des Designs abschneiden). */
export function orderNameForDisplay(raw) {
  return String(raw ?? '').replace(/\s+[0-9a-f]{8}$/i, '').trim();
}

// Kurzlabel ersetzt den Badge-Text, "note" steht als Satz darunter.
const STATUS_TEXT = {
  success: { label: 'Angenommen', note: 'Druckdatei angenommen – du kannst bezahlen.' },
  pending: { label: 'Wird geprüft', note: 'Wird geprüft – dauert meist wenige Minuten.' },
  failed: { label: 'Abgelehnt', note: 'Datei abgelehnt – bitte prüfen und erneut hochladen.' },
  loading: { label: 'Wird geladen', note: '' },
};

// Vendor-/Backend-Texte → Du-Form. Textknoten im Popup und in Toasts,
// nie innerhalb von .flyeralarm-action-link.
const TEXT_MAP = [
  [/Bitte versuchen Sie es später erneut\.?/g, 'Bitte versuch es später noch einmal.'],
  [/Bitte versuchen Sie es erneut\.?/g, 'Bitte versuch es noch einmal.'],
  [/Keine hochgeladenen Produkte gefunden\.?/g, 'In diesem Browser haben wir noch keine hochgeladene Druckdatei gefunden.'],
  [/Fehler beim Laden der Daten\.?/g, 'Das hat nicht geklappt – deine Aufträge konnten nicht geladen werden.'],
  [/Fehler beim Abrufen des Upload-Status\.?/g, 'Der Prüfstatus konnte gerade nicht geladen werden.'],
  [/Fehler beim Laden der Uploads\.?/g, 'Die hochgeladene Datei konnte nicht geöffnet werden.'],
  [/Fehler beim Hochladen der Druckdaten\.?/g, 'Das Hochladen hat nicht geklappt.'],
  [/Fehler beim Aktualisieren der Datei\.?/g, 'Das Ersetzen der Datei hat nicht geklappt.'],
  [/Failed to fetch order data/g, 'Deine Aufträge konnten nicht geladen werden.'],
  [/Failed to duplicate product|Add to cart failed/g, 'Der Artikel konnte nicht in den Warenkorb gelegt werden.'],
  [/API request failed|Unbekannter Fehler/g, 'Das hat nicht geklappt.'],
  [/Only ZIP files are allowed/g, 'Wir brauchen eine ZIP-Datei.'],
  [/File size exceeds (\d+)MB limit/g, 'Die Datei ist größer als $1 MB.'],
  [/Datei erfolgreich hochgeladen/g, 'Deine Datei ist hochgeladen.'],
  [/Kein Upload-Format gefunden/g, 'Für dieses Produkt liegen uns keine Upload-Vorgaben vor.'],
  [/Upload-Anforderungen konnten nicht geladen werden\.?/g, 'Die Upload-Vorgaben konnten nicht geladen werden.'],
  [/Lade Upload-Informationen\.{3}/g, 'Upload-Vorgaben werden geladen…'],
  [/Status not found\.?/g, 'Für diesen Auftrag liegt noch kein Prüfergebnis vor.'],
  [/Unknown upload status\.?/g, 'Der Prüfstatus ist unbekannt.'],
  [/Invalid status response\.?/g, 'Die Antwort der Prüfung war ungültig.'],
];

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DISMISS_KEY = 'dp:upload-banner-dismissed';

/** sessionStorage-Schlüssel und Zeitfenster des Auto-Kasse-Wegs. */
export const AUTO = Object.freeze({
  intentKey: 'dp:order-intent', // schreibt dp-designer-handoff.js bei „Zur Bestellung“
  arrivalKey: 'dp:upload-arrival', // Ankunft über ?view=upload-product-data (Redirect der DP-App)
  cartKeyPrefix: 'dp:auto-cart:', // + orderId → '1', sobald der Countdown einmal lief
  checkoutKey: 'dp:auto-checkout', // orderId, gesetzt kurz vor dem Vendor-Redirect auf /cart
  windowMs: 30 * 60 * 1000,
  cartDelayMs: 5000,
  checkoutDelayMs: 3000,
  slowAfterMs: 10 * 60 * 1000,
});

/* ── Zustand ─────────────────────────────────────────────────────────── */

const statusStore = new Map(); // orderId → { status, reasons[] }
let heldOrdersKnown = null; // null unbekannt · true Aufträge da · false keine
let teeInstalled = false;
let vendorState = 'idle'; // idle · loading · ready · failed (Vendor-Skript, nachgeladen)
let domReady = typeof document !== 'undefined' && document.readyState === 'complete';
let renderQueued = false;
let lastLiveText = '';
let lastFocus = null;
let trapActive = false;
let els = {};
let cfg = {};
const orderMeta = new Map(); // orderId → { createdAt: ms|null, firstSeen: ms }
let autoCart = null; // { orderId, timer, card } während der Countdown läuft
let pendingTimer = 0;
let flipbook = null; // { destroy } aus dp-upload-flipbook.js, solange eine Prüfung läuft
let flipbookLoading = false;
let flipbookUnavailable = false; // kein Abzug im Handoff-Speicher → nicht erneut versuchen
let pdfLink = null; // null unbekannt · false keine PDF im Speicher · { url, bytes, fileName }
let pdfLinkLoading = false;
let lastFlowCounts = null;

// VOR replaceState des Vendor-Skripts lesen (es entfernt ?view= beim Öffnen).
const cameFromUpload = safe(() => new URLSearchParams(window.location.search).get('view') === 'upload-product-data') === true;
// Das Vendor-Skript legt beim ersten Lauf selbst eine Session an — nur ein
// vorher vorhandener Wert zählt als "Besucher mit Upload-Historie".
const hadSession = safe(() => window.localStorage.getItem('buyer_session_id')) !== null;
if (cameFromUpload) safe(() => window.sessionStorage.setItem(AUTO.arrivalKey, String(Date.now())));

/* ── Hilfen ──────────────────────────────────────────────────────────── */

function safe(fn) {
  try { return fn(); } catch (e) { return null; } // prettier-ignore
}

function readConfig(wrapper) {
  const d = (wrapper && wrapper.dataset) || {};
  return {
    bannerEnabled: d.bannerEnabled !== 'false',
    bannerPending: d.bannerPending || 'Deine Druckdatei wird geprüft.',
    bannerFailed: d.bannerFailed || 'Deine Druckdatei wurde abgelehnt – bitte prüfen und erneut hochladen.',
    bannerArrival: d.bannerArrival || 'Fast geschafft: dein Kalender wird geprüft. Sobald die Datei angenommen ist, wird „Kasse“ im Status-Fenster aktiv.',
    bannerNoData: d.bannerNoData || 'Dein Upload-Status ist nur in dem Browser sichtbar, in dem du die Datei hochgeladen hast.',
    bannerButton: d.bannerButton || 'Status ansehen',
    pendingHint: d.pendingHint || 'Wird aktiv, sobald die Druckerei deine Datei angenommen hat.',
    autoCart: d.autoCart !== 'false',
    autoCheckout: d.autoCheckout !== 'false',
    cartUrl: d.cartUrl || '/cart',
    autoCartText: d.autoCartText || 'Druckdatei angenommen – wir legen deinen Kalender in den Warenkorb …',
    autoCheckoutText: d.autoCheckoutText || 'Schritt 3 von 3: Dein Kalender liegt im Warenkorb – gleich geht’s weiter zur Kasse.',
    checkoutNowLabel: d.checkoutNowLabel || 'Jetzt zur Kasse',
    cancelLabel: d.cancelLabel || 'Abbrechen',
    vendorSrc: d.vendorSrc || '',
    // Flipbook „So wird dein Kalender“ während „Wird geprüft“ (dp-upload-flipbook.js)
    flipbook: d.flipbook !== 'false',
    moduleFlipbook: d.moduleFlipbook || '',
    moduleStore: d.moduleStore || '',
    moduleKalendarium: d.moduleKalendarium || '',
    flipbookTitle: d.flipbookTitle || '',
    flipbookLead: d.flipbookLead || '',
    arrivalStage: d.arrivalStage !== 'false',
  };
}

/* ── Reine Helfer (exportiert, Tests in tests/designer/dp-data-upload-shell.test.mjs) ── */

/** Verlängertes Wiederholungsfenster für cart/add (Vendor-Standard 5 × 2 s reicht nicht immer). */
export const CART_RETRY = Object.freeze({ maxAttempts: 15, delayMs: 3000 });

/** Vendor-Optionen mit unserem Fenster zusammenführen; kleinere Vorgaben werden angehoben, größere bleiben. */
export function cartRetryOptions(options, onRetry) {
  const o = options && typeof options === 'object' ? { ...options } : {};
  o.maxAttempts = Math.max(Number(o.maxAttempts) || 0, CART_RETRY.maxAttempts);
  o.delayMs = Math.max(Number(o.delayMs) || 0, CART_RETRY.delayMs);
  const prev = typeof o.onRetry === 'function' ? o.onRetry : null;
  o.onRetry = (info) => {
    if (prev) safe(() => prev(info));
    if (onRetry) onRetry(info);
  };
  return o;
}

/** Text auf dem gesperrten „Kasse“-Link, während Shopify die neue Variante freigibt. */
export function cartRetryLabel(info) {
  const attempt = Number(info && info.attempt) || 0;
  const max = Number(info && info.maxAttempts) || CART_RETRY.maxAttempts;
  return `Wird vorbereitet… (${Math.min(attempt, max)}/${max})`;
}

function cartRetryText(link, info) {
  if (link) link.textContent = cartRetryLabel(info);
}

function cartFailedHint(link, error) {
  const cell = link && link.parentElement;
  if (!cell) return;
  let hint = cell.querySelector(':scope > .dp-upload-hint[data-dp-cart-error]');
  if (!hint) {
    hint = document.createElement('p');
    hint.className = 'dp-upload-hint';
    hint.setAttribute('data-dp-cart-error', '');
    hint.setAttribute('role', 'alert');
    cell.appendChild(hint);
  }
  const status = error && Number(error.status);
  hint.textContent =
    status === 422 || status === 404
      ? 'Shopify hat deinen Kalender noch nicht freigegeben. Bitte in ein paar Sekunden noch einmal auf „Kasse“ klicken.'
      : 'Der Artikel konnte nicht in den Warenkorb gelegt werden. Bitte noch einmal auf „Kasse“ klicken oder die Seite neu laden.';
}

/** 42 000 ms → „0:42“, 3 725 000 ms → „1:02:05“. */
export function formatElapsed(ms) {
  const total = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const sec = String(total % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** Wartezeit-Zeile unter „Wird geprüft“. */
export function pendingProgressText(elapsedMs) {
  const since = formatElapsed(elapsedMs);
  return elapsedMs >= AUTO.slowAfterMs ? `Prüfung läuft seit ${since} · dauert gerade länger als üblich` : `Prüfung läuft seit ${since} · meist 1–3 Minuten`;
}

/** „14.09.2026, 10:42“ (Vendor-Datumszelle, de-DE) → Millisekunden, sonst null. */
export function parseGermanDate(text) {
  const m = /(\d{1,2})\.(\d{1,2})\.(\d{4})(?:,?\s*(\d{1,2}):(\d{2}))?/.exec(String(text || ''));
  if (!m) return null;
  const t = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), Number(m[4] || 0), Number(m[5] || 0)).getTime();
  return Number.isFinite(t) ? t : null;
}

/**
 * Gehört diese Browsersitzung zu einer frischen Bestellung? Ja, wenn der Designer-Merker
 * (dp:order-intent) oder die Ankunft über den Upload-Redirect höchstens windowMs zurückliegt.
 * @param {{ intent?: string|null, arrival?: string|null, now?: number, windowMs?: number }} p
 */
export function sessionEligible({ intent = null, arrival = null, now = Date.now(), windowMs = AUTO.windowMs } = {}) {
  const fresh = (t) => Number.isFinite(t) && t <= now && now - t <= windowMs;
  let intentAt = NaN;
  try {
    const parsed = JSON.parse(intent);
    intentAt = parsed && typeof parsed === 'object' ? Number(parsed.at) : Number(parsed);
  } catch { intentAt = Number(intent); }
  return fresh(intentAt) || fresh(Number(arrival));
}

/**
 * Auftrag für die Auto-Kasse: der jüngste angenommene Auftrag, dessen Erstellungsdatum
 * höchstens windowMs zurückliegt; ohne bekanntes Datum kein Kandidat.
 * @param {Array<{ orderId:string, createdAt:number|null }>} candidates
 * @returns {string|null} orderId
 */
export function pickAutoCartOrder(candidates, { now = Date.now(), windowMs = AUTO.windowMs, designId = null } = {}) {
  // Mit bekannter Design-ID (dp:order-intent) zählt nur der Auftrag, dessen Auftragsname die
  // Kurz-ID trägt („Kalender DIN A4 hoch 569e951e") – nie ein fremder, älterer Auftrag (P2-10).
  const shortId = designId ? String(designId).replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toLowerCase() : null; // wie buildOrderName()
  let best = null;
  for (const c of candidates || []) {
    if (!c || !c.orderId || !Number.isFinite(c.createdAt) || now - c.createdAt > windowMs || c.createdAt - now > 60_000) continue;
    if (shortId && !String(c.orderName || '').toLowerCase().endsWith(shortId)) continue;
    if (!best || c.createdAt > best.createdAt) best = c;
  }
  return best ? String(best.orderId) : null;
}

/**
 * Vendor-Skript nur laden, wenn der Besucher einen Upload-Status haben kann (Technik-Prüfung P1-2).
 * @param {{ cameFromUpload?: boolean, hadSession?: boolean, intent?: string|null, arrival?: string|null, pathname?: string, cartUrl?: string }} p
 */
export function shouldLoadVendor({ cameFromUpload = false, hadSession = false, intent = null, arrival = null, pathname = '', cartUrl = '/cart' } = {}) {
  return Boolean(cameFromUpload) || Boolean(hadSession) || Boolean(intent) || Boolean(arrival) || isCartPath(pathname, cartUrl);
}

/**
 * Schwebender Trigger „Deine Druckdatei“ nur für Besucher mit Upload-Historie (P1-7).
 * @param {{ hadSession?: boolean, intent?: string|null, arrival?: string|null, heldOrdersKnown?: boolean|null, pathname?: string }} p
 */
export function triggerVisible({ hadSession = false, intent = null, arrival = null, heldOrdersKnown = null, pathname = '' } = {}) {
  if (/\/pages\/designer(?:\/|$)/.test(String(pathname || ''))) return false;
  if (heldOrdersKnown === true) return true;
  return Boolean(hadSession) || Boolean(intent) || Boolean(arrival);
}

/** /cart, /cart/, /de/cart → Warenkorbseite (Vergleich gegen routes.cart_url, sonst Pfadende). */
export function isCartPath(pathname, cartUrl = '/cart') {
  const norm = (p) => String(p || '').replace(/\/+$/, '') || '/';
  const path = norm(pathname);
  return path === norm(cartUrl) || /\/cart$/.test(path);
}

const isVisible = (el) => !!el && el.getClientRects().length > 0;

const normalizeStatus = (raw) => (raw === 'Upload successful' ? 'success' : raw === 'Pending' ? 'pending' : 'failed');

function statusFromBadge(badge) {
  if (!badge) return null;
  const c = badge.classList;
  if (c.contains('flyeralarm-status-valid') || c.contains('flyeralarm-status-erfolg') || c.contains('flyeralarm-status-ERFOLG')) return 'success';
  if (c.contains('flyeralarm-status-ausstehend') || c.contains('flyeralarm-status-pending')) return 'pending';
  if (c.contains('flyeralarm-status-abgelehnt')) return 'failed';
  if (c.contains('flyeralarm-status-loading')) return 'loading';
  return null;
}

const translateText = (text) => TEXT_MAP.reduce((out, [pattern, replacement]) => out.replace(pattern, replacement), text);

function translateTextNodes(root) {
  if (!root) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parent = node.parentElement;
      if (!node.nodeValue || !node.nodeValue.trim() || !parent) return NodeFilter.FILTER_REJECT;
      if (parent.closest(S.actionLink + ', .dp-upload-note, .dp-upload-hint, .dp-upload-auto, .dp-upload-banner')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  for (const node of nodes) {
    const next = translateText(node.nodeValue);
    if (next !== node.nodeValue) node.nodeValue = next;
  }
}

function reasonsFromTooltip(badge) {
  const tip = badge && badge.querySelector('.flyeralarm-tooltip-text');
  if (!tip) return [];
  return tip.innerHTML
    .split(/<br\s*\/?>\s*<br\s*\/?>/i)
    .map((s) => s.replace(/<[^>]+>/g, '').trim())
    .filter(Boolean);
}

/* ── Tee auf die Vendor-API ─────────────────────────────────────────── */

function installTee() {
  // Globale lexikalische Bindung des Vendor-Skripts (class-Deklaration).
  const api = safe(() => (typeof UploadApiService === 'function' ? UploadApiService : null)); // eslint-disable-line no-undef
  if (!api || typeof api.getBulkUploadStatus !== 'function') return false;

  // Der Vendor fragt den Prüfstatus alle 10 s ab; das Ergebnis erscheint so bis zu 10 s zu spät.
  // UPLOAD_CONFIG ist eine globale const-Bindung (Objekt bleibt änderbar) und wird beim Start des
  // Intervalls gelesen (data-upload.js startStatusRefresh) – also hier vor dem Öffnen anpassen.
  safe(() => { if (typeof UPLOAD_CONFIG === 'object' && UPLOAD_CONFIG) UPLOAD_CONFIG.statusRefreshInterval = STATUS_REFRESH_MS; }); // eslint-disable-line no-undef

  const origBulk = api.getBulkUploadStatus;
  api.getBulkUploadStatus = async function dpTeeBulk(...args) {
    const res = await origBulk.apply(this, args);
    safe(() => ingestBulk(res)); // Hülle darf den Vendor-Fluss nie stören
    return res;
  };
  if (typeof api.addToCart === 'function') {
    // Shopify meldet eine frisch duplizierte Variante für einige Sekunden als „ausverkauft“ (422);
    // der Vendor versucht es nur 5 × 2 s. Wir verlängern das Fenster und zeigen den Fortschritt.
    const origCart = api.addToCart;
    api.addToCart = async function dpTeeCart(cartFormData, options) {
      const link = safe(() => els.block.querySelector(S.actionLink + '.flyeralarm-disabled'));
      const opts = cartRetryOptions(options, (info) => safe(() => cartRetryText(link, info)));
      try {
        // Stückzahl als Positions-Eigenschaft: der Vendor legt jede Position mit Menge 1 und dem
        // Gesamtpreis der gewählten Staffel an; die Menge steht nur im Handoff-Eintrag (Auftragsname
        // trägt die ersten 8 Zeichen der designId). snippets/dp-cart-item.liquid zeigt _Menge als Pille.
        await annotateCartQuantity(cartFormData).catch(() => null);
        return await origCart.call(this, cartFormData, opts);
      } catch (error) {
        safe(() => cartFailedHint(link, error));
        throw error;
      }
    };
  }
  if (typeof api.fetchOrderData === 'function') {
    const origFetch = api.fetchOrderData;
    api.fetchOrderData = async function dpTeeHeld(...args) {
      const res = await origFetch.apply(this, args);
      safe(() => ingestHeld(res, args[1]));
      return res;
    };
  }
  return true;
}

/** Ergänzt properties._Menge (und _Preis je Stück) aus dem Handoff-Eintrag, der zum Auftragsnamen passt. */
export function quantityForOrderName(orderName, entries) {
  const m = /([A-Za-z0-9]{8})\s*$/.exec(String(orderName ?? '').trim());
  if (!m) return null;
  const short = m[1].toLowerCase();
  const hit = (entries ?? []).find((e) => String(e?.designId ?? '').replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toLowerCase() === short);
  const qty = Number(hit?.quantity);
  return Number.isInteger(qty) && qty > 0 ? qty : null;
}

async function annotateCartQuantity(cartFormData) {
  const items = cartFormData && Array.isArray(cartFormData.items) ? cartFormData.items : [];
  if (!items.length || !cfg.moduleStore) return;
  const storeMod = await import(/* webpackIgnore: true */ cfg.moduleStore);
  const entries = await storeMod.getHandoffStore().list();
  for (const item of items) {
    const qty = quantityForOrderName(item?.properties?.['_Order Name'], entries);
    if (qty && qty > 1) item.properties = { ...(item.properties ?? {}), _Menge: String(qty) };
  }
}

function ingestBulk(res) {
  for (const item of res && Array.isArray(res.data) ? res.data : []) {
    if (!item || !item.order_id) continue;
    const data = item.data;
    statusStore.set(String(item.order_id), {
      status: data ? normalizeStatus(data.order_upload_status) : 'failed',
      reasons: data && Array.isArray(data.reasons) ? data.reasons.map(String) : [],
    });
  }
  queueRender();
}

function ingestHeld(res, page) {
  if (page && Number(page) > 1) return;
  const top = res && res.data;
  const held = Array.isArray(top) ? top : top && Array.isArray(top.data) ? top.data : [];
  heldOrdersKnown = held.length > 0;
  for (const o of held) {
    const id = String((o && o.flyer_order_id) || '');
    if (!id) continue;
    const t = Date.parse(o.created_at || o.createdAt || o.created_date || '');
    const meta = orderMeta.get(id) || { createdAt: null, firstSeen: Date.now() };
    if (Number.isFinite(t)) meta.createdAt = t;
    orderMeta.set(id, meta);
  }
  queueRender();
}

function metaFor(orderId, row) {
  let meta = orderMeta.get(orderId);
  if (!meta) {
    meta = { createdAt: null, firstSeen: Date.now() };
    orderMeta.set(orderId, meta);
  }
  if (meta.createdAt == null && row) {
    const cell = row.querySelector('.flyeralarm-create-date');
    meta.createdAt = cell ? parseGermanDate(cell.textContent) : null;
  }
  return meta;
}

/* ── Rendern (gebündelt je Frame) ───────────────────────────────────── */

function queueRender() {
  if (renderQueued) return;
  renderQueued = true;
  requestAnimationFrame(() => {
    renderQueued = false;
    safe(processTable);
    safe(renderBanner);
    safe(syncTrigger);
  });
}

/** Trigger zeigen/verbergen; das Vendor-Skript hängt ihn an <body>, deshalb über document suchen. */
function syncTrigger() {
  const trigger = document.querySelector(S.trigger);
  if (!trigger) return;
  const visible = vendorState === 'ready' && triggerVisible({
    hadSession,
    intent: safe(() => window.sessionStorage.getItem(AUTO.intentKey)),
    arrival: safe(() => window.sessionStorage.getItem(AUTO.arrivalKey)),
    heldOrdersKnown,
    pathname: window.location.pathname,
  });
  if (trigger.hidden !== !visible) trigger.hidden = !visible;
}

function headerLabels() {
  if (!els.labels) els.labels = Array.from(els.block.querySelectorAll(S.thead)).map((th) => th.textContent.trim());
  return els.labels;
}

function processTable() {
  const tbody = els.block.querySelector(S.tbody);
  if (!tbody) return;
  const labels = headerLabels();
  const rows = tbody.querySelectorAll('tr');
  const counts = { pending: 0, failed: 0, success: 0 };
  const accepted = [];

  rows.forEach((row) => {
    const cells = row.querySelectorAll(':scope > td');
    if (cells.length === 1 && cells[0].hasAttribute('colspan')) {
      row.classList.add('dp-upload-row--empty');
      return;
    }
    cells.forEach((td, i) => {
      if (labels[i] && td.getAttribute('data-label') !== labels[i]) td.setAttribute('data-label', labels[i]);
      if (COLUMNS[i] && td.getAttribute('data-dp-col') !== COLUMNS[i]) td.setAttribute('data-dp-col', COLUMNS[i]);
    });

    const badge = row.querySelector(S.badge);
    const status = statusFromBadge(badge);
    if (!status) return;

    // Gründe: bevorzugt aus dem Tee (roh), sonst aus dem Vendor-Tooltip.
    const orderId = String(row.dataset.orderId || '');
    const stored = orderId ? statusStore.get(orderId) : null;
    let reasons = [];
    if (status === 'failed') {
      reasons = stored && stored.status === 'failed' && stored.reasons.length ? stored.reasons : reasonsFromTooltip(badge);
      reasons = reasons.map(translateText);
    }
    if (!teeInstalled && orderId && status !== 'loading') statusStore.set(orderId, { status, reasons });
    if (counts[status] !== undefined) counts[status] += 1;
    if (orderId && status === 'success') accepted.push({ orderId, createdAt: metaFor(orderId, row).createdAt, orderName: row.dataset.orderName || '' });
    if (orderId && status === 'pending') metaFor(orderId, row);

    renderBadgeLabel(badge, status);
    renderNote(badge, status, reasons, orderId);
    renderAction(row, status);
    renderOrderName(row);
  });

  translateTextNodes(els.block);
  updateLiveRegion(counts, rows.length);
  syncPendingTimer(counts.pending > 0);
  safe(() => syncFlipbook(counts.pending > 0)); // auch wenn ältere Aufträge schon angenommen sind
  safe(() => renderFlowCard(counts));
  maybeScheduleAutoCart(accepted);
}

/* ── Schritt-Anzeige im Status-Fenster (Kunde dieser Sitzung) ───────────── */

/** Text und aktiver Schritt aus den Zählern: laufende Prüfung vor Angenommen. */
export function flowStateFor(counts, fromDesigner) {
  if (!counts) return null;
  if (counts.pending > 0) {
    return { active: 'check', note: 'Schritt 2 von 3: Deine Druckdatei wird geprüft. Sobald sie angenommen ist, legen wir den Kalender automatisch in den Warenkorb.' };
  }
  if (counts.success > 0) return { active: 'pay', note: 'Schritt 3 von 3: Druckdatei angenommen – jetzt nur noch bezahlen.' };
  if (counts.failed > 0) return { active: 'check', note: fromDesigner ? 'Schritt 2 von 3: Die Druckdatei wurde abgelehnt. Prüfe die Hinweise und erzeuge sie im Designer noch einmal.' : 'Schritt 2 von 3: Die Druckdatei wurde abgelehnt. Prüfe die Hinweise und lade sie noch einmal hoch.' };
  return null;
}

function renderFlowCard(counts) {
  lastFlowCounts = counts;
  const body = els.container.querySelector('.flyeralarm-popup-body');
  if (!body) return;
  const intent = safe(() => window.sessionStorage.getItem(AUTO.intentKey));
  const arrival = safe(() => window.sessionStorage.getItem(AUTO.arrivalKey));
  const eligible = sessionEligible({ intent, arrival: Number(arrival) });
  const fromDesigner = Boolean(intentDesignId(intent));
  const state = eligible ? flowStateFor(counts, fromDesigner) : null;
  let card = body.querySelector(':scope > .dp-upload-flow');
  if (!state) { if (card) card.remove(); return; }
  if (fromDesigner) ensurePdfLink(intentDesignId(intent));
  const key = state.active + '|' + state.note + '|' + (pdfLink ? pdfLink.url : '');
  if (card && card.getAttribute('data-dp-key') === key) return;
  if (!card) {
    card = document.createElement('div');
    card.className = 'dp-upload-flow';
    body.insertBefore(card, body.firstChild);
  }
  card.setAttribute('data-dp-key', key);
  card.innerHTML = renderFlowSteps({ variant: fromDesigner ? 'design' : 'upload', active: state.active, note: state.note }) + renderPdfLink(pdfLink);
}

/** Link auf die im Browser erzeugte Druckdatei (Handoff-Speicher) – der Kunde sieht vor dem Bezahlen genau das, was gedruckt wird. */
export function renderPdfLink(link) {
  if (!link || !link.url) return '';
  const mb = link.bytes / 1_000_000;
  const size = link.bytes > 0 ? ` (PDF, ${(mb < 10 ? mb.toFixed(1) : String(Math.round(mb))).replace('.', ',')} MB)` : ' (PDF)';
  return '<p class="dp-upload-flow__file"><a href="' + escapeAttr(link.url) + '" target="_blank" rel="noopener">Deine Druckdatei ansehen' + size + '</a><span class="dp-upload-flow__file-hint"> – öffnet in einem neuen Tab, genau so wird gedruckt.</span></p>';
}

function escapeAttr(value) {
  return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

/** Druckdatei der Bestellung dieser Sitzung einmal aus dem Handoff-Speicher holen (Blob-URL bleibt für die Seitenlaufzeit). */
function ensurePdfLink(designId) {
  if (pdfLink !== null || pdfLinkLoading || !designId || !cfg.moduleStore) return;
  pdfLinkLoading = true;
  (async () => {
    const storeMod = await import(/* webpackIgnore: true */ cfg.moduleStore);
    const entry = await storeMod.getHandoffStore().get(designId);
    const blob = entry?.blob;
    if (!(blob instanceof Blob) || !blob.size) { pdfLink = false; return; }
    pdfLink = { url: URL.createObjectURL(blob), bytes: blob.size, fileName: entry.fileName || 'kalender.pdf' };
    if (lastFlowCounts) safe(() => renderFlowCard(lastFlowCounts));
  })().catch((error) => { console.warn('[dp-data-upload-shell] Druckdatei nicht verfügbar.', error); pdfLink = false; }).finally(() => { pdfLinkLoading = false; });
}

/* ── Flipbook „So wird dein Kalender“ (Wartezeit der Prüfung überbrücken) ── */

/** Design-ID der Bestellung dieser Sitzung (dp:order-intent, geschrieben von dp-designer-handoff.js). */
export function intentDesignId(raw) {
  try {
    const v = JSON.parse(raw);
    const id = String(v?.designId ?? '');
    return /^[A-Za-z0-9-]{8,64}$/.test(id) ? id : null;
  } catch { return null; }
}

function syncFlipbook(show) {
  if (!cfg.flipbook || !cfg.moduleFlipbook || !cfg.moduleStore) return;
  if (!show) { if (flipbook) { flipbook.destroy(); flipbook = null; } return; }
  if (flipbook) {
    // Der Vendor baut die Statusansicht alle 10 s neu – Karte ggf. wieder einhängen.
    if (!flipbook.card.isConnected) {
      const host = els.container.querySelector('.flyeralarm-status-view') || els.container;
      host.insertBefore(flipbook.card, host.firstChild);
    }
    return;
  }
  if (flipbookLoading || flipbookUnavailable) return;
  const designId = intentDesignId(safe(() => window.sessionStorage.getItem(AUTO.intentKey)));
  if (!designId) { flipbookUnavailable = true; return; }
  flipbookLoading = true;
  loadFlipbook(designId).catch((error) => { console.warn('[dp-data-upload-shell] Flipbook nicht verfügbar.', error); flipbookUnavailable = true; }).finally(() => { flipbookLoading = false; });
}

async function loadFlipbook(designId) {
  const storeMod = await import(/* webpackIgnore: true */ cfg.moduleStore);
  const store = storeMod.getHandoffStore();
  const entry = await store.get(designId);
  const snapshot = entry?.preview;
  if (!snapshot || !Array.isArray(snapshot.pages) || !snapshot.pages.length) { flipbookUnavailable = true; return; }
  const [fb, kal] = await Promise.all([
    import(/* webpackIgnore: true */ cfg.moduleFlipbook),
    cfg.moduleKalendarium ? import(/* webpackIgnore: true */ cfg.moduleKalendarium).catch(() => null) : null,
  ]);
  const renderCalendarium = (page) => {
    if (!kal || typeof kal.renderMonthCalendarium !== 'function' || !page.month) return '';
    const c = snapshot.calendar || {};
    // Eigene Termine des Monats (snapshot.events, dp-design-snapshot.js) — wie auf der Bühne.
    const events = (Array.isArray(snapshot.events) ? snapshot.events : []).filter((e) => e && e.month === page.month).map((e) => ({ day: e.day, label: e.label }));
    // Stil (snapshot.calendar.style, dp-kalendarium-styles.js) gilt vor variant; Bandhöhe der Seite für die Auto-Umschaltung.
    return kal.renderMonthCalendarium({ month: page.month, year: page.year ?? snapshot.year, formatKey: snapshot.formatKey, variant: c.variant, style: c.style || undefined, bandHeightMm: page.calendarium?.heightMm, farben: c.farben || undefined, showWeekNumbers: c.showWeekNumbers, showHolidays: c.showHolidays, region: c.region || undefined, events, weekStart: 'monday', locale: 'de-DE' });
  };
  // Zwischenzeitlich angenommen/geschlossen? Dann nicht mehr einhängen.
  if (!els.block.querySelector('.dp-upload-note__timer')) return;
  const host = els.container.querySelector('.flyeralarm-status-view') || els.container;
  const texts = {};
  if (cfg.flipbookTitle) texts.title = cfg.flipbookTitle;
  if (cfg.flipbookLead) texts.lead = cfg.flipbookLead;
  flipbook = fb.createFlipbook({ host, snapshot, renderCalendarium, texts });
}

function renderBadgeLabel(badge, status) {
  const label = STATUS_TEXT[status].label;
  let textNode = null;
  for (const n of badge.childNodes) {
    if (n.nodeType !== Node.TEXT_NODE || !n.nodeValue.trim()) continue;
    if (textNode) n.nodeValue = '';
    else textNode = n;
  }
  if (!textNode) badge.insertBefore(document.createTextNode(label), badge.firstChild);
  else if (textNode.nodeValue !== label) textNode.nodeValue = label;
  badge.setAttribute('data-dp-status', status);
}

/** Auftragsname (Format aus dem Designer) als zweite Zeile unter dem gekürzten Produktnamen. */
function renderOrderName(row) {
  const cell = row.querySelector('td[data-dp-col="product-name"]');
  const name = orderNameForDisplay(row.dataset.orderName);
  if (!cell || !name) return;
  let line = cell.querySelector(':scope > .dp-upload-order');
  if (!line) {
    line = document.createElement('span');
    line.className = 'dp-upload-order';
    cell.appendChild(line);
  }
  if (line.textContent !== name) line.textContent = name;
}

function renderNote(badge, status, reasons, orderId = '') {
  const cell = badge.parentElement;
  if (!cell) return;
  const key = status + '|' + reasons.join('') + '|' + orderId;
  let note = cell.querySelector(':scope > .dp-upload-note');
  if (note && note.getAttribute('data-dp-key') === key) return;
  if (!note) {
    note = document.createElement('div');
    note.className = 'dp-upload-note';
    cell.appendChild(note);
  }
  note.setAttribute('data-dp-key', key);
  note.setAttribute('data-dp-status', status);
  note.textContent = '';
  const text = STATUS_TEXT[status].note;
  note.hidden = !text;
  // Gründe stehen sichtbar da; der Hover-Tooltip des Vendors bleibt im DOM
  // (Listener hängen daran) und wird nur per CSS ausgeblendet.
  cell.classList.toggle('dp-upload-cell--reasons', reasons.length > 0);
  if (!text) return;
  note.appendChild(Object.assign(document.createElement('p'), { className: 'dp-upload-note__text', textContent: text }));
  if (reasons.length) {
    const ul = Object.assign(document.createElement('ul'), { className: 'dp-upload-note__reasons' });
    for (const r of reasons) ul.appendChild(Object.assign(document.createElement('li'), { textContent: r }));
    note.appendChild(ul);
  }
  if (status === 'failed' && intentDesignId(safe(() => window.sessionStorage.getItem(AUTO.intentKey)))) {
    // Rückweg für Designer-Kunden: Der Vendor bietet nur „Erneut hochladen" (ZIP) – Laien
    // korrigieren im Designer und erzeugen die Druckdatei neu (Kaufweg-Prüfung 2026-09-16, P2-3).
    const back = Object.assign(document.createElement('a'), { className: 'dp-upload-note__back', href: '/pages/designer', textContent: 'Im Designer korrigieren und neu erzeugen' });
    note.appendChild(back);
  }
  if (status === 'pending' && orderId) {
    // Lebende Wartezeit; tickt sichtbar je Sekunde, bleibt für Screenreader still (die Live-Region meldet Statuswechsel).
    const timer = Object.assign(document.createElement('p'), { className: 'dp-upload-note__timer', textContent: '' });
    timer.setAttribute('data-dp-order', orderId);
    timer.setAttribute('aria-hidden', 'true');
    note.appendChild(timer);
    tickPendingTimers();
  }
}

/* ── Wartezeit je „Wird geprüft“-Zeile ──────────────────────────────── */

function tickPendingTimers() {
  const now = Date.now();
  els.block.querySelectorAll('.dp-upload-note__timer').forEach((el) => {
    const meta = orderMeta.get(el.getAttribute('data-dp-order') || '');
    const since = meta ? (meta.createdAt ?? meta.firstSeen) : now;
    const text = pendingProgressText(now - since);
    if (el.textContent !== text) el.textContent = text;
  });
}

function syncPendingTimer(anyPending) {
  const open = !!els.overlay && els.overlay.classList.contains('active');
  if (anyPending && open) {
    if (!pendingTimer) pendingTimer = window.setInterval(() => safe(tickPendingTimers), 1000);
  } else if (pendingTimer) {
    window.clearInterval(pendingTimer);
    pendingTimer = 0;
  }
}

/* ── Auto-Kasse: angenommener Auftrag dieser Sitzung → Countdown → Vendor-„Kasse“ ── */

function maybeScheduleAutoCart(accepted) {
  if (!cfg.autoCart || autoCart || !accepted.length) return;
  if (!els.overlay.classList.contains('active')) return; // nur im offenen Popup (die Zeile muss da sein)
  const intent = safe(() => window.sessionStorage.getItem(AUTO.intentKey));
  const eligible = sessionEligible({ intent, arrival: safe(() => window.sessionStorage.getItem(AUTO.arrivalKey)) });
  if (!eligible) return;
  const orderId = pickAutoCartOrder(accepted, { designId: intentDesignId(intent) });
  if (!orderId || autoCartDone(orderId)) return;
  const link = actionLinkFor(orderId);
  if (!link) return;
  markAutoCartDone(orderId); // einmal je Auftrag – auch über Tabs hinweg (localStorage, 24 h)
  startAutoCart(orderId);
}

/** Sperre je Auftrag: Tab-Sitzung UND browserweit mit Zeitstempel (zweiter Tab darf nicht doppelt kaufen). */
function autoCartDone(orderId) {
  if (safe(() => window.sessionStorage.getItem(AUTO.cartKeyPrefix + orderId)) === '1') return true;
  const at = Number(safe(() => window.localStorage.getItem(AUTO.cartKeyPrefix + orderId)));
  return Number.isFinite(at) && at > 0 && Date.now() - at < 24 * 60 * 60 * 1000;
}

function markAutoCartDone(orderId) {
  safe(() => window.sessionStorage.setItem(AUTO.cartKeyPrefix + orderId, '1'));
  safe(() => window.localStorage.setItem(AUTO.cartKeyPrefix + orderId, String(Date.now())));
}

/** Merker für /cart → Kasse: nur mit Zeitstempel und nur wenige Minuten gültig (P2-10). */
export function checkoutMarker(raw, { now = Date.now(), maxAgeMs = 10 * 60 * 1000 } = {}) {
  try {
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object' || !v.orderId) return null;
    const at = Number(v.at);
    return Number.isFinite(at) && now - at >= 0 && now - at <= maxAgeMs ? String(v.orderId) : null;
  } catch { return null; }
}

function actionLinkFor(orderId) {
  const row = els.block.querySelector(`tr[data-order-id="${CSS.escape(orderId)}"]`);
  const link = row && row.querySelector(S.actionLink);
  return link && link.textContent.trim() === 'Kasse' && !link.classList.contains('flyeralarm-disabled') ? link : null;
}

function startAutoCart(orderId) {
  const card = document.createElement('div');
  card.className = 'dp-upload-auto';
  card.setAttribute('data-dp-order', orderId);
  const text = Object.assign(document.createElement('p'), { className: 'dp-upload-auto__text', textContent: cfg.autoCartText });
  text.setAttribute('role', 'status');
  const bar = document.createElement('span');
  bar.className = 'dp-upload-auto__bar';
  bar.setAttribute('aria-hidden', 'true');
  bar.appendChild(document.createElement('i'));
  const count = Object.assign(document.createElement('span'), { className: 'dp-upload-auto__count', textContent: '5 s' });
  count.setAttribute('aria-hidden', 'true');
  const cancel = Object.assign(document.createElement('button'), { type: 'button', className: 'dp-upload-auto__cancel', textContent: cfg.cancelLabel });
  cancel.setAttribute('aria-label', `${cfg.cancelLabel} – nicht automatisch in den Warenkorb legen`);
  cancel.addEventListener('click', () => {
    stopAutoCart();
    const link = actionLinkFor(orderId);
    if (link) link.focus({ preventScroll: true });
  });
  card.append(text, bar, count, cancel);
  const host = els.container.querySelector('.flyeralarm-status-view') || els.container;
  host.insertBefore(card, host.firstChild);
  cancel.focus({ preventScroll: true });

  const endsAt = Date.now() + AUTO.cartDelayMs;
  const tick = () => {
    const left = Math.max(0, endsAt - Date.now());
    count.textContent = `${Math.ceil(left / 1000)} s`;
    bar.firstChild.style.width = `${100 - (left / AUTO.cartDelayMs) * 100}%`;
    if (left > 0) return;
    stopAutoCart();
    const link = actionLinkFor(orderId);
    if (!link) return;
    safe(() => window.sessionStorage.setItem(AUTO.checkoutKey, JSON.stringify({ orderId, at: Date.now() })));
    link.click(); // Vendor: handleAddToCart(row) → duplicate → cart/add → /cart
  };
  autoCart = { orderId, card, timer: window.setInterval(() => safe(tick), 250) };
  tick();
}

function stopAutoCart() {
  if (!autoCart) return;
  window.clearInterval(autoCart.timer);
  autoCart.card.remove();
  autoCart = null;
}

/* ── /cart: genau eine Position und gerade über „Kasse“ gekommen → /checkout ── */

function initCartAutoCheckout() {
  if (!cfg.autoCheckout || !isCartPath(window.location.pathname, cfg.cartUrl)) return;
  const orderId = checkoutMarker(safe(() => window.sessionStorage.getItem(AUTO.checkoutKey)));
  if (!orderId) return;
  safe(() => window.sessionStorage.removeItem(AUTO.checkoutKey)); // nur einmal
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .then((cart) => {
      if (!cart || !Array.isArray(cart.items) || cart.items.length !== 1) return;
      showCheckoutBanner(`${root}checkout`);
    })
    .catch(() => null);
}

function showCheckoutBanner(checkoutUrl) {
  const banner = document.createElement('div');
  banner.className = 'dp-upload-banner';
  banner.setAttribute('data-dp-variant', 'checkout');
  banner.setAttribute('role', 'region');
  banner.setAttribute('aria-label', 'Weiter zur Kasse');
  const text = Object.assign(document.createElement('p'), { className: 'dp-upload-banner__text', textContent: cfg.autoCheckoutText });
  text.setAttribute('role', 'status');
  const count = Object.assign(document.createElement('span'), { className: 'dp-upload-auto__count', textContent: '3 s' });
  count.setAttribute('aria-hidden', 'true');
  const go = Object.assign(document.createElement('a'), { className: 'dp-upload-banner__btn', href: checkoutUrl, textContent: cfg.checkoutNowLabel });
  const cancel = Object.assign(document.createElement('button'), { type: 'button', className: 'dp-upload-banner__btn dp-upload-banner__btn--quiet', textContent: cfg.cancelLabel });
  banner.append(text, count, go, cancel);
  document.body.insertBefore(banner, document.body.firstChild);
  const endsAt = Date.now() + AUTO.checkoutDelayMs;
  const timer = window.setInterval(() => {
    const left = Math.max(0, endsAt - Date.now());
    count.textContent = `${Math.ceil(left / 1000)} s`;
    if (left > 0) return;
    window.clearInterval(timer);
    window.location.assign(checkoutUrl);
  }, 250);
  cancel.addEventListener('click', () => {
    window.clearInterval(timer);
    banner.remove();
  });
  go.addEventListener('click', () => window.clearInterval(timer));
  // Kein Fokusraub beim Laden (Technik-Prüfung P2-16): role="status" am Text meldet die Leiste, Tab erreicht die Knöpfe.
}

function renderAction(row, status) {
  const link = row.querySelector(S.actionLink);
  if (!link) return;
  const cell = link.parentElement;
  const inactive = status === 'pending' || status === 'loading';
  link.classList.toggle('dp-upload-action--primary', status === 'success');
  link.classList.toggle('dp-upload-action--secondary', status === 'failed');
  for (const [attr, value] of [['aria-disabled', 'true'], ['tabindex', '-1']]) {
    if (inactive) link.setAttribute(attr, value);
    else link.removeAttribute(attr);
  }
  if (!cell) return;
  let hint = cell.querySelector(':scope > .dp-upload-hint');
  if (status === 'pending' && !hint) {
    hint = Object.assign(document.createElement('div'), { className: 'dp-upload-hint', textContent: cfg.pendingHint });
    cell.appendChild(hint);
  }
  if (hint) hint.hidden = status !== 'pending';
}

function updateLiveRegion(counts, rowCount) {
  const live = els.block.querySelector(S.live);
  if (!live || !rowCount) return;
  const parts = [];
  if (counts.pending) parts.push(counts.pending === 1 ? 'Eine Druckdatei wird geprüft.' : counts.pending + ' Druckdateien werden geprüft.');
  if (counts.success) parts.push(counts.success === 1 ? 'Eine Druckdatei ist angenommen – du kannst bezahlen.' : counts.success + ' Druckdateien sind angenommen.');
  if (counts.failed) parts.push(counts.failed === 1 ? 'Eine Druckdatei wurde abgelehnt.' : counts.failed + ' Druckdateien wurden abgelehnt.');
  const text = parts.join(' ');
  if (text && text !== lastLiveText) {
    lastLiveText = text;
    live.textContent = text;
  }
}

/* ── Hinweisleiste (Wiedereinstieg) ─────────────────────────────────── */

/** Aufträge, die älter als dieses Fenster sind, steuern die Leiste nicht mehr (alte Tests, längst erledigte Ablehnungen). */
export const BANNER_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** Zählt offene/abgelehnte Aufträge; Alter unbekannt zählt mit, zu alt fällt raus. */
export function bannerCounts(entries, now = Date.now(), maxAgeMs = BANNER_MAX_AGE_MS) {
  let pending = 0;
  let failed = 0;
  for (const e of entries || []) {
    if (!e) continue;
    const t = Number(e.createdAt);
    if (Number.isFinite(t) && t > 0 && now - t > maxAgeMs) continue;
    if (e.status === 'pending') pending += 1;
    if (e.status === 'failed') failed += 1;
  }
  return { pending, failed };
}

function bannerVariant() {
  const entries = [];
  statusStore.forEach((v, orderId) => {
    const meta = orderMeta.get(orderId);
    entries.push({ status: v.status, createdAt: meta ? meta.createdAt : null });
  });
  let { pending, failed } = bannerCounts(entries);
  // Ohne Tee: Vendor-Badge am Trigger signalisiert ungesehene Prüfungen.
  if (!teeInstalled && !statusStore.size) {
    const nb = document.querySelector(S.notificationBadge);
    if (nb && nb.style.display && nb.style.display !== 'none') pending = 1;
  }
  if (cameFromUpload && heldOrdersKnown === false) return 'nodata';
  if (failed) return 'failed';
  if (pending) return cameFromUpload ? 'arrival' : 'pending';
  return null;
}

function renderBanner() {
  if (!cfg.bannerEnabled || !hadSession) return;
  if (safe(() => window.sessionStorage.getItem(DISMISS_KEY)) === '1') return;
  const variant = bannerVariant();
  let banner = document.querySelector('.dp-upload-banner:not([data-dp-variant="checkout"])');
  if (!variant) {
    if (banner) banner.remove();
    return;
  }
  if (!banner) banner = createBanner();
  if (banner.getAttribute('data-dp-variant') === variant) return;
  banner.setAttribute('data-dp-variant', variant);
  banner.querySelector('.dp-upload-banner__text').textContent = { pending: cfg.bannerPending, failed: cfg.bannerFailed, arrival: cfg.bannerArrival, nodata: cfg.bannerNoData }[variant];
  banner.querySelector('.dp-upload-banner__btn').hidden = variant === 'nodata';
}

function createBanner() {
  const banner = document.createElement('div');
  banner.className = 'dp-upload-banner';
  banner.setAttribute('role', 'region');
  banner.setAttribute('aria-label', 'Hinweis zu deiner Druckdatei');

  const text = document.createElement('p');
  text.className = 'dp-upload-banner__text';
  const btn = Object.assign(document.createElement('button'), { type: 'button', className: 'dp-upload-banner__btn', textContent: cfg.bannerButton });
  btn.addEventListener('click', () => {
    lastFocus = btn;
    openPopup();
  });
  const close = Object.assign(document.createElement('button'), { type: 'button', className: 'dp-upload-banner__close' });
  close.setAttribute('aria-label', 'Hinweis schließen');
  close.innerHTML = '<span aria-hidden="true">&times;</span>';
  close.addEventListener('click', () => {
    safe(() => window.sessionStorage.setItem(DISMISS_KEY, '1'));
    banner.remove();
  });
  banner.append(text, btn, close);
  document.body.insertBefore(banner, document.body.firstChild);
  return banner;
}

function openPopup() {
  if (typeof window.openFlyeralarmPopup === 'function') return window.openFlyeralarmPopup();
  const trigger = document.querySelector(S.trigger);
  if (trigger) trigger.click();
}

/* ── Vendor-Skript nachladen ─────────────────────────────────────────── */

function loadVendor() {
  if (vendorState !== 'idle' || !cfg.vendorSrc) return;
  if (safe(() => typeof UploadApiService === 'function')) { vendorState = 'ready'; return; } // eslint-disable-line no-undef
  vendorState = 'loading';
  const script = document.createElement('script');
  script.src = cfg.vendorSrc;
  script.async = true;
  script.addEventListener('load', () => { vendorState = 'ready'; safe(bootVendor); safe(syncTrigger); queueRender(); }, { once: true });
  script.addEventListener('error', () => {
    vendorState = 'failed';
    console.warn('[dp-data-upload-shell] Das Upload-Status-Skript konnte nicht geladen werden; der Status-Knopf bleibt verborgen.');
    safe(syncTrigger);
  }, { once: true });
  document.head.appendChild(script);
}

/** Tee installieren und – falls DOMContentLoaded schon vorbei ist – die Vendor-Instanz selbst erzeugen. */
function bootVendor() {
  teeInstalled = installTee();
  // Der Vendor registriert new FlyeralarmDataUpload() auf DOMContentLoaded; nach dem Event feuert das nie.
  if (!domReady) return;
  if (safe(() => typeof FlyeralarmDataUpload === 'function' && typeof window.openFlyeralarmPopup !== 'function')) new FlyeralarmDataUpload(); // eslint-disable-line no-undef
}

/* ── Dialog: Fokus, Fokusfalle, Rückgabe ────────────────────────────── */

function onOverlayToggle() {
  const active = els.overlay.classList.contains('active');
  if (active && !trapActive) {
    trapActive = true;
    if (!lastFocus || !document.contains(lastFocus)) lastFocus = document.activeElement;
    els.overlay.addEventListener('keydown', handleDialogKeydown);
    window.setTimeout(() => {
      if (!els.overlay.classList.contains('active')) return;
      const target = els.container.querySelector(S.title) || els.container.querySelector(S.closeBtn) || els.container;
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }, 50);
  } else if (!active && trapActive) {
    trapActive = false;
    stopAutoCart();
    els.overlay.removeEventListener('keydown', handleDialogKeydown);
    const back = lastFocus && document.contains(lastFocus) && isVisible(lastFocus) ? lastFocus : document.querySelector(S.trigger);
    lastFocus = null;
    if (isVisible(back)) back.focus({ preventScroll: true });
  }
  syncPendingTimer(!!els.block.querySelector('.dp-upload-note__timer'));
  if (active) queueRender();
}

/** Escape schließt über den Vendor-Schließen-Knopf (Technik-Prüfung P2-16); Tab bleibt im Dialog. */
function handleDialogKeydown(event) {
  if (event.key === 'Escape' || event.key === 'Esc') {
    const close = els.container.querySelector(S.closeBtn);
    if (!close) return;
    event.preventDefault();
    event.stopPropagation(); // sonst schließt der Document-Listener des Vendors ein zweites Mal
    close.click();
    return;
  }
  trapTab(event);
}

function trapTab(event) {
  if (event.key !== 'Tab') return;
  const focusable = Array.from(els.container.querySelectorAll(FOCUSABLE)).filter(
    (el) => isVisible(el) && el.getAttribute('aria-disabled') !== 'true',
  );
  if (!focusable.length) {
    event.preventDefault();
    els.container.focus();
    return;
  }
  const current = document.activeElement;
  const idx = focusable.indexOf(current);
  // idx === -1: Fokus liegt auf Titel/Container (tabindex=-1) oder außerhalb.
  if (event.shiftKey && idx <= 0) {
    event.preventDefault();
    focusable[focusable.length - 1].focus();
  } else if (!event.shiftKey && (idx === focusable.length - 1 || !els.container.contains(current))) {
    event.preventDefault();
    focusable[0].focus();
  }
}

/* ── Start ───────────────────────────────────────────────────────────── */

/* ── Ankunftsseite nach der Übertragung ─────────────────────────────────
   Die DP-App leitet nach finalizeOrder auf /?view=upload-product-data (Startseite) um. Statt
   Hero und Formaten zeigt die Seite dann eine ruhige Bühne: Schritt-Anzeige, was gerade passiert,
   Knopf zum Status-Fenster (Kaufweg-Prüfung 2026-09-16, P2-2). Die Startseite bleibt darunter
   erreichbar („Zur Startseite" entfernt nur die Klasse). */
function renderArrivalStage() {
  const main = document.getElementById('MainContent') || document.querySelector('main');
  if (!main || document.querySelector('.dp-upload-stage')) return;
  document.body.classList.add('dp-upload-arrival');
  const stage = document.createElement('section');
  stage.className = 'dp-upload-stage';
  stage.setAttribute('aria-labelledby', 'dp-upload-stage-title');
  const fromDesigner = Boolean(intentDesignId(safe(() => window.sessionStorage.getItem(AUTO.intentKey))));
  stage.innerHTML = `
    <div class="dp-upload-stage__inner">
      <p class="dp-upload-stage__eyebrow">Fast geschafft</p>
      <h1 class="dp-upload-stage__title" id="dp-upload-stage-title">Deine Druckdatei wird geprüft</h1>
      ${renderFlowSteps({ variant: fromDesigner ? 'design' : 'upload', active: 'check', note: 'Die Druckerei prüft deine Datei – meist ein bis drei Minuten. Sobald sie angenommen ist, legen wir den Kalender in den Warenkorb und du bezahlst an der Kasse.' })}
      <div class="dp-upload-stage__actions">
        <button type="button" class="dp-upload-stage__btn dp-upload-stage__btn--primary" data-dp-stage-open>Status anzeigen</button>
        ${fromDesigner ? '<a class="dp-upload-stage__btn" href="/pages/designer">Weiter gestalten</a>' : ''}
        <button type="button" class="dp-upload-stage__link" data-dp-stage-home>Zur Startseite</button>
      </div>
      <p class="dp-upload-stage__hint">Du kannst diese Seite geöffnet lassen – der Status aktualisiert sich von selbst. Bezahlt wird erst an der Kasse, bis dahin entsteht keine Bestellung.</p>
    </div>`;
  main.insertBefore(stage, main.firstChild);
  stage.querySelector('[data-dp-stage-open]').addEventListener('click', () => { lastFocus = stage.querySelector('[data-dp-stage-open]'); openPopup(); });
  stage.querySelector('[data-dp-stage-home]').addEventListener('click', () => { document.body.classList.remove('dp-upload-arrival'); stage.remove(); window.scrollTo(0, 0); });
}

function init() {
  cfg = readConfig(document.querySelector(S.wrapper));
  safe(initCartAutoCheckout);
  if (cameFromUpload && cfg.arrivalStage) safe(renderArrivalStage);
  const block = document.querySelector(S.block);
  if (!block) return;
  els = { block, overlay: block.querySelector(S.overlay), container: block.querySelector(S.container) };
  if (!els.overlay || !els.container) return;

  const trigger = block.querySelector(S.trigger) || document.querySelector(S.trigger);
  if (trigger) trigger.addEventListener('click', () => (lastFocus = trigger));
  safe(syncTrigger);

  // Zeilen werden vom Vendor-Skript eingefügt und alle 10 s erneuert.
  const tbody = block.querySelector(S.tbody);
  if (tbody) {
    new MutationObserver(queueRender).observe(tbody, { childList: true, subtree: true });
    // Manueller Klick auf „Kasse“: gleicher Merker wie die Auto-Kasse, damit /cart weiterleiten darf.
    tbody.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest(S.actionLink);
      if (!link || link.textContent.trim() !== 'Kasse' || link.classList.contains('flyeralarm-disabled')) return;
      const row = link.closest('tr');
      if (row && row.dataset.orderId) safe(() => window.sessionStorage.setItem(AUTO.checkoutKey, JSON.stringify({ orderId: row.dataset.orderId, at: Date.now() })));
    }, true);
  }

  // Öffnen/Schließen: Vendor toggelt die Klasse "active" am Overlay.
  new MutationObserver(onOverlayToggle).observe(els.overlay, { attributes: true, attributeFilter: ['class'] });

  // Reupload-Ansicht (Formatinfo, Fehlertexte) und Toasts: Texte in Du-Form.
  const reupload = block.querySelector('.flyeralarm-reupload-view');
  if (reupload) new MutationObserver(() => translateTextNodes(reupload)).observe(reupload, { childList: true, subtree: true });
  new MutationObserver((records) => {
    for (const r of records) {
      r.addedNodes.forEach((n) => n.nodeType === Node.ELEMENT_NODE && n.classList.contains('flyeralarm-toast') && translateTextNodes(n));
    }
  }).observe(document.body, { childList: true });

  // Ohne Tee: Vendor-Benachrichtigungs-Badge als Signal für die Leiste.
  if (!teeInstalled) {
    const nb = block.querySelector(S.notificationBadge);
    if (nb) new MutationObserver(queueRender).observe(nb, { attributes: true, attributeFilter: ['style'] });
  }

  const wanted = shouldLoadVendor({
    cameFromUpload,
    hadSession,
    intent: safe(() => window.sessionStorage.getItem(AUTO.intentKey)),
    arrival: safe(() => window.sessionStorage.getItem(AUTO.arrivalKey)),
    pathname: window.location.pathname,
    cartUrl: cfg.cartUrl,
  });
  if (wanted) loadVendor();

  queueRender();
}

if (typeof document !== 'undefined') {
  if (!domReady) document.addEventListener('DOMContentLoaded', () => { domReady = true; }, { once: true });
  teeInstalled = installTee(); // greift nur, wenn das Vendor-Skript ausnahmsweise schon statisch geladen ist
  if (teeInstalled) vendorState = 'ready';
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
}
